import React, { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import EditIcon from "@mui/icons-material/Edit";
import LockResetIcon from "@mui/icons-material/LockReset";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { Dash, Workspace } from "../../../types/Dash";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import useActions, { useAppSelector } from "../../../app/hooks";

import { selectLibrary } from "../../library/librarySlice";
import { Host } from "../../../types/Host";

export default function HostList(props: {
  parentCloseHandle: () => void;
  //currentWorkspace: Workspace | null;
}) {
  const actions = useActions();

  const [createMode, setCreateMode] = React.useState<boolean>(false);
  const [editMode, setEditMode] = React.useState<boolean>(false);
  const [passwordResetMode, setPasswordResetMode] = React.useState<boolean>(false);
  const [selectedHost, setSelectedHost] = React.useState<Host | null>(null);
  const [resetPassword, setResetPassword] = useState("");
  const [resetPasswordError, setResetPasswordError] = React.useState("");

  const [name, setName] = useState("");
  const [nameError, setNameError] = React.useState("");
  const [url, setURL] = useState("");
  const [urlError, setURLError] = React.useState("");
  const [domain, setDomain] = useState("");
  const [domainError, setDomainError] = React.useState("");
  const [ip, setIP] = useState("");
  const [ipError, setIPError] = React.useState("");
  const [port, setPort] = useState("");
  const [portError, setPortError] = React.useState("");
  const [username, setUsername] = useState("");
  const [usernameError, setUsernameError] = React.useState("");
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = React.useState("");


  const librarySlice = useAppSelector(selectLibrary);

  // Grab the current hosts for the workspace
  const override = useEffect(() => {
      actions.getHosts();
    }, [])

  function handleNameChange(e: any) {
    if (e.target.value.trim() === "") {
      setNameError("Invalid name");
    } else setNameError("");
    setName(e.target.value);
  }

  function handleURLChange(e: any) {
    setURL(e.target.value);
  }

  function handleDomainChange(e: any) {
    setDomain(e.target.value);
  }

  function handleIPChange(e: any) {
    setIP(e.target.value);
  }

  function handlePortChange(e: any) {
    setPort(e.target.value);
  }

  function handleUsernameChange(e: any) {
    setUsername(e.target.value);
  }

  function handlePasswordChange(e: any) {
    setPassword(e.target.value);
  }

  function handleSelectAutomation(e: any) {
    props.parentCloseHandle();
  }

  function resetFormFields() {
    setName("");
    setNameError("");
    setURL("");
    setURLError("");
    setDomain("");
    setDomainError("");
    setIP("");
    setIPError("");
    setPort("");
    setPortError("");
    setUsername("");
    setUsernameError("");
    setPassword("");
    setPasswordError("");
    setResetPassword("");
    setResetPasswordError("");
    setCreateMode(false);
    setEditMode(false);
    setPasswordResetMode(false);
    setSelectedHost(null);
  }

  function handleCreateHost() {
    actions.createHost({
      name: name,
      url: url || null,
      domain: domain || null,
      ip: ip || null,
      port: port ? parseInt(port) : 0,
      username: username || null,
      password: password || null,
    });
    resetFormFields();
  }

  function handleEditHost(host: Host) {
    setSelectedHost(host);
    setEditMode(true);
    setName(host.name || "");
    setURL(host.url || "");
    setDomain(host.domain || "");
    setIP(host.ip || "");
    setPort(host.port?.toString() || "");
    setUsername(host.username || "");
    // Don't set password - it's not editable
  }

  function handleUpdateHost() {
    if (selectedHost) {
      actions.updateHost({
        hostId: selectedHost.id,
        hostUpdateRequest: {
          name: name,
          url: url || null,
          domain: domain || null,
          ip: ip || null,
          port: port ? parseInt(port) : 0,
          username: username || null,
        }
      });
      resetFormFields();
    }
  }

  function handlePasswordReset(host: Host) {
    setSelectedHost(host);
    setPasswordResetMode(true);
    setResetPassword("");
  }

  function handleResetPassword() {
    if (selectedHost) {
      if (resetPassword.trim() === "") {
        setResetPasswordError("Password is required");
        return;
      }
      actions.resetHostPassword({
        hostId: selectedHost.id,
        hostPasswordResetRequest: {
          password: resetPassword,
        }
      });
      resetFormFields();
    }
  }

  function handleResetPasswordChange(e: any) {
    if (e.target.value.trim() === "") {
      setResetPasswordError("Password is required");
    } else {
      setResetPasswordError("");
    }
    setResetPassword(e.target.value);
  }

  return (
    <Dialog open={true} onClose={() => props.parentCloseHandle()} maxWidth="sm" fullWidth>
      <DialogTitle>Hosts</DialogTitle>
      
      <DialogContent>
        { !createMode && !editMode && !passwordResetMode && (
          <Box sx={{ pt: 2 }}>
            { librarySlice.hosts.length === 0 && (
              <Typography sx={{ mb: 2, textAlign: "center" }}>No hosts in the workspace...</Typography>
            )}

          <List>
              { librarySlice.hosts.map(host => (
                <ListItem 
                  key={'host_deets_' + host.id}
                  sx={{ py: 1.5 }}
                  secondaryAction={
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <IconButton 
                        edge="end" 
                        onClick={() => handlePasswordReset(host)}
                        size="small"
                        title="Reset Password"
                      >
                        <LockResetIcon />
                      </IconButton>
                      <IconButton 
                        edge="end" 
                        onClick={() => handleEditHost(host)}
                        size="small"
                        title="Edit Host"
                      >
                        <EditIcon />
                      </IconButton>
                    </Box>
                  }
                > 
                  <ListItemText primary={host.name} />
                </ListItem>
              ))}
        </List>
          </Box>
        )}
        { createMode && (
          <Box component="form" sx={{ pt: 2 }}>
            <DialogContentText sx={{ mb: 3 }}>Create a new host</DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="name"
            label="Host name"
            error={nameError !== ""}
              helperText={nameError}
            fullWidth
            variant="standard"
              value={name}
            onChange={handleNameChange}
              sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            id="url"
            label="URL"
            error={urlError !== ""}
              helperText={urlError}
            fullWidth
            variant="standard"
              value={url}
            onChange={handleURLChange}
              sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            id="domain"
            label="Domain name"
            error={domainError !== ""}
              helperText={domainError}
            fullWidth
            variant="standard"
              value={domain}
            onChange={handleDomainChange}
              sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            id="IP"
            label="IP"
            error={ipError !== ""}
              helperText={ipError}
            fullWidth
            variant="standard"
              value={ip}
            onChange={handleIPChange}
              sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            id="Port"
            label="Port"
            error={portError !== ""}
              helperText={portError}
            fullWidth
            variant="standard"
              value={port}
            onChange={handlePortChange}
              sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            id="Username"
            label="Username"
            error={usernameError !== ""}
              helperText={usernameError}
            fullWidth
            variant="standard"
              value={username}
            onChange={handleUsernameChange}
              sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            id="Password"
            label="Password"
            error={passwordError !== ""}
              helperText={passwordError}
            fullWidth
            variant="standard"
              type="password"
              value={password}
            onChange={handlePasswordChange}
              sx={{ mb: 2 }}
            />
          </Box>
        )}
        { editMode && (
          <Box component="form" sx={{ pt: 2 }}>
            <DialogContentText sx={{ mb: 3 }}>Edit host</DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              id="name"
              label="Host name"
              error={nameError !== ""}
              helperText={nameError}
              fullWidth
              variant="standard"
              value={name}
              onChange={handleNameChange}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              id="url"
              label="URL"
              error={urlError !== ""}
              helperText={urlError}
              fullWidth
              variant="standard"
              value={url}
              onChange={handleURLChange}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              id="domain"
              label="Domain name"
              error={domainError !== ""}
              helperText={domainError}
              fullWidth
              variant="standard"
              value={domain}
              onChange={handleDomainChange}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              id="IP"
              label="IP"
              error={ipError !== ""}
              helperText={ipError}
              fullWidth
              variant="standard"
              value={ip}
              onChange={handleIPChange}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              id="Port"
              label="Port"
              error={portError !== ""}
              helperText={portError}
              fullWidth
              variant="standard"
              value={port}
              onChange={handlePortChange}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              id="Username"
              label="Username"
              error={usernameError !== ""}
              helperText={usernameError}
              fullWidth
              variant="standard"
              value={username}
              onChange={handleUsernameChange}
              sx={{ mb: 2 }}
            />
          </Box>
        )}
        { passwordResetMode && (
          <Box component="form" sx={{ pt: 2 }}>
            <DialogContentText sx={{ mb: 3 }}>
              Reset password for {selectedHost?.name}
            </DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              id="resetPassword"
              label="New Password"
              error={resetPasswordError !== ""}
              helperText={resetPasswordError}
              fullWidth
              variant="standard"
              type="password"
              value={resetPassword}
              onChange={handleResetPasswordChange}
              sx={{ mb: 2 }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
        <Button 
          onClick={() => {
            if (createMode || editMode || passwordResetMode) {
              resetFormFields();
            } else {
              props.parentCloseHandle();
            }
          }}
        >
          Cancel
        </Button>
        { !createMode && !editMode && !passwordResetMode && (
          <Button
          variant="contained"
          color="primary"
            onClick={() => setCreateMode(true)}
          >
            Create Host
          </Button>
        )}
        { createMode && (
          <Button
          variant="contained"
          color="primary"
            onClick={() => handleCreateHost()}
          >
            Confirm
          </Button>
        )}
        { editMode && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleUpdateHost()}
          >
            Update
          </Button>
        )}
        { passwordResetMode && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleResetPassword()}
          >
            Reset Password
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
